/**
 * Cookie consent for logged out visitors, as recorded by OneTrust.
 *
 * Logged in users have agreed to our terms, so `useConsent` grants them
 * everything. Logged out visitors get nothing beyond strictly necessary
 * cookies until they opt in through the OneTrust banner.
 */

export interface Consent {
  // analytics, e.g. GA4, Amplitude, Segment
  performance: boolean;
  // advertising, e.g. Google Ads, LinkedIn
  targeting: boolean;
}

export const FULL_CONSENT: Consent = { performance: true, targeting: true };

// OneTrust category IDs, see the OneTrust admin for what lives in each
const PERFORMANCE_GROUP = 'C0002';
const TARGETING_GROUP = 'C0004';

const ONETRUST_DOMAIN_SCRIPT = 'bd9564d9-5534-4188-8249-1f3cd852a89f';
const ONETRUST_GROUPS_UPDATED = 'OneTrustGroupsUpdated';

export const parseConsent = (
  activeGroups: string | undefined,
  globalPrivacyControl: boolean | undefined,
): Consent => {
  const groups = (activeGroups ?? '').split(',');
  return {
    performance: groups.includes(PERFORMANCE_GROUP),
    // GPC is a legal opt out of sale/sharing, so it overrides an accept click
    targeting: groups.includes(TARGETING_GROUP) && !globalPrivacyControl,
  };
};

// Cookies and storage keys OneTrust's cookie list doesn't name, as prefixes.
// Matches the consent bridge on the marketing sites, which share this domain.
const UNLISTED_PREFIXES: Record<string, string[]> = {
  // Studio only: `_dd_s` covers Datadog RUM's `_dd_s_v2` and its legacy `_dd_s`
  [PERFORMANCE_GROUP]: ['AMP_', 'amplitude', 'ph_', '_cltk', '_dd_s'],
  [TARGETING_GROUP]: [
    'utm_',
    'first_known_',
    'gclid',
    '_gcl_',
    'li_',
    'claydar_',
    'radar_',
  ],
};

/**
 * Counsel (2026-10-29): a category the visitor hasn't allowed must leave
 * nothing behind. Deletes the first-party cookies OneTrust lists for each
 * disallowed category, plus `UNLISTED_PREFIXES`, on this host and its parent
 * domains, and the matching local/session storage keys. Cookies on other
 * companies' domains and HttpOnly cookies are out of reach.
 */
export const purgeDisallowed = (consent: Consent) => {
  try {
    const disallowed = [
      ...(consent.performance ? [] : [PERFORMANCE_GROUP]),
      ...(consent.targeting ? [] : [TARGETING_GROUP]),
    ];
    const prefixes = disallowed.flatMap((group) => UNLISTED_PREFIXES[group]);
    const names: string[] = [];
    window.OneTrust?.GetDomainData?.()?.Groups?.forEach((group) => {
      if (!disallowed.includes(group.CustomGroupId ?? '')) return;
      group.FirstPartyCookies?.forEach(({ Name }) => {
        if (!Name) return;
        // OneTrust writes a wildcard as a run of x's, as in _ga_xxxxxxxxxx
        const prefix = Name.replace(/x{6,}.*/, '');
        // an empty prefix would match every key, OneTrust's own included
        if (!prefix) return;
        if (prefix === Name) names.push(Name);
        else prefixes.push(prefix);
      });
    });
    const matches = (key: string) =>
      names.includes(key) || prefixes.some((prefix) => key.startsWith(prefix));

    // a cookie can be host-only or set on any parent domain; clear each form
    const labels = window.location.hostname.split('.');
    const domains = [
      '',
      ...labels
        .slice(0, -1)
        .map((_, i) => `; domain=${labels.slice(i).join('.')}`),
    ];
    document.cookie.split(';').forEach((part) => {
      const name = part.replace(/=.*/, '').trim();
      if (!name || !matches(name)) return;
      domains.forEach((domain) => {
        document.cookie = `${name}=; max-age=0; path=/${domain}`;
      });
    });

    // the real stores, not the memory fallback; this whole block is in a try
    // eslint-disable-next-line no-restricted-properties
    [window.localStorage, window.sessionStorage].forEach((storage) => {
      Object.keys(storage)
        .filter(matches)
        .forEach((key) => storage.removeItem(key));
    });
  } catch {
    // never let cleanup break consent handling
  }
};

let lastGpcEnforced: string | undefined;

/**
 * OneTrust lets an explicit Accept override GPC ("manual consent takes
 * precedence"), but counsel requires GPC to win. `parseConsent` already drops
 * Targeting under GPC; this records the opt out in OneTrust too, so its
 * toggle, consent cookie and records of consent agree. UpdateConsent fires
 * another consent event, which then sees no change.
 */
export const enforceGpcInOneTrust = (
  globalPrivacyControl: boolean | undefined,
) => {
  const groups = window.OnetrustActiveGroups ?? '';
  if (!globalPrivacyControl || !groups.split(',').includes(TARGETING_GROUP)) {
    // Targeting is off, so a later Accept with the same groups is new again
    lastGpcEnforced = undefined;
    return;
  }
  // stops a loop if OneTrust ever ignores the update
  if (groups === lastGpcEnforced) return;
  lastGpcEnforced = groups;
  try {
    window.OneTrust?.UpdateConsent?.('Category', `${TARGETING_GROUP}:0`);
  } catch {
    // the bridge-side filtering in parseConsent still applies
  }
};

/**
 * Calls `callback` whenever OneTrust records a consent choice, including once
 * when its SDK first loads. Returns an unsubscribe function.
 */
export const onConsentChange = (callback: () => void) => {
  window.addEventListener(ONETRUST_GROUPS_UPDATED, callback);
  return () => window.removeEventListener(ONETRUST_GROUPS_UPDATED, callback);
};

let oneTrustScript: HTMLScriptElement | undefined;

/**
 * Injects the OneTrust banner SDK. Safe to call more than once; `onLoad` only
 * runs for the first call.
 */
export const loadOneTrust = (onLoad?: () => void) => {
  if (oneTrustScript) return;
  oneTrustScript = document.createElement('script');
  if (onLoad) oneTrustScript.onload = onLoad;
  oneTrustScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
  oneTrustScript.setAttribute('charset', 'UTF-8');
  oneTrustScript.setAttribute('data-domain-script', ONETRUST_DOMAIN_SCRIPT);
  document.head.appendChild(oneTrustScript);
};
